import { useCallback, useState } from "react";
import { apiV2 } from "../../../api/axiosConfig";
import { PaginatedResponse } from "../../../types/PaginatedResponse";
import { DeviceDataTypes } from "../../../types/device-data-types";
import { generateAndSaveXLSX } from "../import/useDownloadXLSX";
import { useAuth } from "../../../context/user";
import { useTimeZoneSelectContext } from "../../../providers/TimeZoneSelectProvider";
import moment from "moment-timezone";

// Fetch devices paginated
export const fetchDevices = async (
  clientID: string,
  page: number,
  limit: number,
  archived: boolean,
  departmentIDs: string[],
  search: string
): Promise<PaginatedResponse<DeviceDataTypes>> => {
  let url = `devices?clientID=${clientID}&page=${page}&limit=${limit}`;

  if (archived) {
    url += "&archived=true";
  }

  if (departmentIDs.length > 0) {
    url += `&departmentID=${departmentIDs.join(",")}`;
  }

  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  const response = await apiV2.get(url);
  return response.data;
};

export const useDeviceExport = (
  clientID: string,
  departmentIDs: string[],
  search: string
) => {
  const auth = useAuth();
  const tzCtx = useTimeZoneSelectContext();
  const [downloading, setDownloading] = useState(false);

  const download = useCallback(async () => {
    if (!clientID && auth.isSuperAdmin) return;

    setDownloading(true);
    const devices: DeviceDataTypes[] = [];
    let page = 1;
    const rpp = 200;
    let hasMore = true;

    try {
      while (hasMore) {
        const res = await fetchDevices(clientID, page, rpp, false, departmentIDs, search);
        if (res && res.items.length > 0) {
          devices.push(...res.items);
          page++;
        } else {
          hasMore = false;
        }
      }
    } catch (e) {
      console.error(e);
      setDownloading(false);
      return;
    }

    const headers = [
      "ID",
      "Name",
      "DeviceID / IMEI",
      "Client",
      "Department",
      "Type",
      "Last Activity At",
      "Assigned User",
      "Device Data Source API",
      "Ownership Started At",
    ];

    const rows = devices.map((d): string[] => {
      return [
        d.id,
        d.name,
        d.deviceID || "",
        d.clientName || "",
        d.departmentName || "",
        d.type || "",
        d.lastActivityAt
          ? moment.tz(d.lastActivityAt, tzCtx.timeZone).format("YYYY-MM-DD HH:mm:ss")
          : "",
        d.UserName || "",
        d.deviceSourceAPI || "",
        d.ownershipStartedAt
          ? moment.tz(d.ownershipStartedAt, tzCtx.timeZone).format("YYYY-MM-DD")
          : "",
      ];
    });
    
    

    generateAndSaveXLSX("device-export.xlsx", headers, rows);
    setDownloading(false);
  }, [clientID, departmentIDs, search]);

  return { download, downloading };
};

